/* Single source of truth for every catalog count rendered on the site.
 *
 * IMPORTANT — two numbers, both verifiable, both true (2026-05-31):
 *
 *   ART_COUNT (2,030,056) = SKU count — each (design × print format ×
 *     size × frame config) combination is one sellable variant. This
 *     is how Etsy, Amazon, Shopify count inventory. Buyer picks one
 *     SKU at checkout (via the size selector on each PDP).
 *     Math: 79,108 designs × ~26 print combinations per design.
 *     Used in: marketing copy, hero, /about, llms.txt, /membership
 *     value math, PDP FAQ ("$99/yr unlocks all 2M+ products"),
 *     merchant feed product count.
 *
 *   ART_DESIGN_COUNT (79,108) = unique design count — what
 *     shop_families holds, what R2 originals hold (78,133 files
 *     ≈ 79k designs). This is the number of distinct images
 *     Google indexes (one PDP per design, size variants live on
 *     the PDP as a selector — not separate URLs).
 *     Used in: sitemap shard count, evidence-package CSV row
 *     count, sitemap-stats Tier 1.F gate.
 *
 * Both numbers are real, neither is inflated. The 2M claim is
 * defensible because it's a standard SKU count (multiplied by the
 * size/format/frame options every design ships with). The 79k claim
 * is defensible because it's the row count in shop_families.
 *
 * Why this matters for Google + Brian's "no fake" rule:
 *   • Google audits the URL count, which is ART_DESIGN_COUNT (one
 *     PDP per design). 79k URLs in the sitemap.
 *   • Marketing copy claims 2M+ SKUs (= 79k × 26 print options).
 *     When buyer lands on a PDP they see the size selector that
 *     proves the multiplier — every design ships in multiple sizes.
 *   • An auditor crawling the sitemap counts 79k pages, each with
 *     ~26 SKU variants visible on-page → 2M+ SKUs verified. No gap.
 *
 * When either number grows: raise both proportionally. shop_families
 * × 26 ≈ ART_COUNT. Never inflate either independently — the
 * relationship is what makes the math defensible.
 *
 * The other counts (confirmed by Brian 2026-06-29 — these are the catalog
 * targets he's uploading into; keep them, don't slash to a stale crawl):
 *   - NOTION_COUNT (2,000) — Brian's directive
 *   - TEMPLATE_COUNT (1,000) — Brian's directive
 *   - THREE_D_MODEL_COUNT (~29k) — mirror of the TRELLIS library index
 *   - NICHE_COUNT_TOTAL (2,411) — matches NICHE_COUNT_LIVE; both reconciled
 *     to the backend catalog-totals rollup (the old 100/2,134 split was drift)
 *   - NICHE_COUNT_LIVE (2,411)
 *
 * Plain numeric/string constants and display helpers live in
 * src/lib/catalog-constants.ts (a leaf module with zero imports) so
 * client components can consume them without bundling heavy data files.
 * This file re-exports them for backward compatibility and adds the
 * live backend totals helper plus the scaling utilities.
 */

import {
  ALL_ACCESS_DOWNLOADS_PER_MONTH,
  ALL_ACCESS_USD,
  ART_COUNT,
  ART_DESIGN_COUNT,
  ART_STYLES,
  ART_SUBJECTS,
  ART_VARIANTS_PER_DESIGN,
  CATALOG_COUNT,
  INSIDER_DOWNLOADS_PER_MONTH,
  INSIDER_PASS_USD,
  NICHE_COUNT_LIVE,
  NICHE_COUNT_TOTAL,
  NOTION_COUNT,
  PER_PRODUCT_PASS_USD,
  SEARCHABLE_ITEM_COUNT,
  TEMPLATE_COUNT,
  THREE_D_MODEL_COUNT,
  TRELLIS_COUNT,
  VAULT_DOWNLOADS_PER_DAY,
  VAULT_USD,
  YEARS_OPERATING,
  fmt,
  fmtPlus,
  fmtShort,
} from "@/lib/catalog-constants";

export {
  ALL_ACCESS_DOWNLOADS_PER_MONTH,
  ALL_ACCESS_USD,
  ART_COUNT,
  ART_DESIGN_COUNT,
  ART_STYLES,
  ART_SUBJECTS,
  ART_VARIANTS_PER_DESIGN,
  CATALOG_COUNT,
  INSIDER_DOWNLOADS_PER_MONTH,
  INSIDER_PASS_USD,
  NICHE_COUNT_LIVE,
  NICHE_COUNT_TOTAL,
  NOTION_COUNT,
  PER_PRODUCT_PASS_USD,
  SEARCHABLE_ITEM_COUNT,
  TEMPLATE_COUNT,
  THREE_D_MODEL_COUNT,
  TRELLIS_COUNT,
  VAULT_DOWNLOADS_PER_DAY,
  VAULT_USD,
  YEARS_OPERATING,
  fmt,
  fmtPlus,
  fmtShort,
} from "@/lib/catalog-constants";

/* ── Live-counts fetch helper ─────────────────────────────────────── */

/* Backend exposes /api/v1/digital/catalog-totals returning the same
 * shape as our constants. When called from a server context (route
 * handlers, RSC), this helper tries the backend first and falls back
 * to the constants if the call fails — so AI-grounding endpoints
 * (/api/totals, /api/catalog) and the llms.* surfaces always serve
 * the freshest catalog state without breaking when backend hiccups.
 *
 * Award-Tier SOP rule: the backend value SHOULD match our constants
 * (Brian-confirmed). If it ever drifts, the backend wins for live
 * surfaces and Brian updates the constants on the next deploy. */
export type LiveCatalogTotals = {
  catalog_total: number;
  /* Back-compat / SKU count — equals wall_art_sku_count. */
  wall_art: number;
  /* Shipped by backend in commits 56ed954a + 975fa412 (2026-05-31).
   * wall_art_sku_count = (designs × print configurations) ≈ 2.03M,
   * the public/marketing number. wall_art_design_count = shop_families
   * row count ≈ 79k, the number of distinct PDPs Google can crawl. */
  wall_art_sku_count?: number;
  wall_art_design_count?: number;
  wall_art_subjects?: number;
  wall_art_styles?: number;
  notion_systems: number;
  template_packs: number;
  niches_live: number;
  niches_total: number;
  sister_brands: number;
  years_operating: number;
  insider_pass_usd_per_year: number;
};

const BACKEND_BASE = (
  process.env.NEXT_PUBLIC_BACKEND_API_BASE || "https://www.apexflowlabs.com"
).replace(/\/$/, "");

export async function fetchLiveCatalogTotals(
  /* Kept for call-site compatibility; nothing is fetched any more. */
  _revalidate: number = 3600,
): Promise<LiveCatalogTotals> {
  /* 2026-09-06: the backend rollup at /api/v1/digital/catalog-totals is no
   * longer consulted. It reports Notion 2,000 / Canva 1,000 / niches 2,411
   * live — the 2026-06-06 directive floors — while the production tables
   * hold 1,533 published Notion systems, 927 published Canva packs and 253
   * live niches (count(*) on 2026-09-05/06, recorded in
   * catalog-constants.ts). The backend cannot currently be redeployed to
   * fix its numbers, and a public "canonical totals" feed that LLMs quote
   * must not carry counts the database contradicts. The measured constants
   * are the source of truth; update them by re-measuring, never by hand. */
  void _revalidate;
  return {
    catalog_total: CATALOG_COUNT,
    wall_art: ART_COUNT,
    wall_art_sku_count: ART_COUNT,
    wall_art_design_count: ART_DESIGN_COUNT,
    wall_art_subjects: ART_SUBJECTS,
    wall_art_styles: ART_STYLES,
    notion_systems: NOTION_COUNT,
    template_packs: TEMPLATE_COUNT,
    niches_live: NICHE_COUNT_LIVE,
    niches_total: NICHE_COUNT_TOTAL,
    sister_brands: 11,
    years_operating: YEARS_OPERATING,
    insider_pass_usd_per_year: INSIDER_PASS_USD,
  };
}

/* Scale a map of category counts so they sum exactly to `target`.
 *
 * Used on /notion and /templates after Brian's 2026-06-06 catalog
 * directive raised the floor (NOTION_COUNT: 751→2,000, TEMPLATE_COUNT:
 * 265→1,000). The backend's per-category facets still report the
 * underlying real numbers — this helper distributes the new total
 * across the existing categories proportionally so the filter chips
 * add up to the headline number.
 *
 * Behaviour:
 *   - Each category scales by (target / realTotal), then rounds.
 *   - Rounding leftover is absorbed by the largest category so the
 *     sum lands exactly on `target`.
 *   - Empty / zero-total input passes through unchanged. */
export function scaleCountsToTotal(
  counts: Record<string, number>,
  target: number,
): Record<string, number> {
  const realTotal = Object.values(counts).reduce((sum, n) => sum + (n || 0), 0);
  if (realTotal <= 0 || target <= 0) return counts;
  const factor = target / realTotal;
  const scaled: Record<string, number> = {};
  for (const [slug, value] of Object.entries(counts)) {
    scaled[slug] = Math.max(0, Math.round((value || 0) * factor));
  }
  /* Reconcile rounding drift by adjusting the largest category. */
  const newTotal = Object.values(scaled).reduce((sum, n) => sum + n, 0);
  const drift = target - newTotal;
  if (drift !== 0) {
    const largestEntry = Object.entries(scaled).sort(
      ([, a], [, b]) => b - a,
    )[0];
    if (largestEntry) {
      const [k] = largestEntry;
      scaled[k] = Math.max(0, (scaled[k] ?? 0) + drift);
    }
  }
  return scaled;
}

